import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserResponse } from '../../lib/users';
import { organisationsApi } from '../../lib/organisations';
import { Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface UsersListProps {
  users: UserResponse[];
  onEdit: (user: UserResponse) => void;
  onDelete: (id: string) => void;
  currentUserId?: string;
  isDeleting: boolean;
}

export default function UsersList({
  users,
  onEdit,
  onDelete,
  currentUserId,
  isDeleting,
}: UsersListProps) {
  const { data: organisations } = useQuery({
    queryKey: ['organisations'],
    queryFn: () => organisationsApi.getAll(),
  });

  const organisationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (organisations ?? []).forEach((o) => map.set(o.id, o.name));
    return map;
  }, [organisations]);

  const handleRowClick = (user: UserResponse, e: React.MouseEvent) => {
    // Don't trigger edit if clicking on action buttons
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    onEdit(user);
  };
  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (role) {
      case 'super_user':
        return 'default';
      case 'site_admin':
        return 'secondary';
      case 'end_user':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (users.length === 0) {
    return (
      <Card className="text-center py-12" data-testid="users-list-empty">
        <p className="text-muted-foreground">No users available</p>
      </Card>
    );
  }

  return (
    <Card data-testid="users-list-container">
      <div className="overflow-x-auto" data-testid="users-list-scroll-container">
        <Table data-testid="users-table" id="users-table">
          <TableHeader data-testid="users-table-header">
            <TableRow>
              <TableHead className="px-3 sm:px-4 md:px-6" data-testid="users-table-header-email">
                Email
              </TableHead>
              <TableHead className="hidden sm:table-cell px-3 sm:px-4 md:px-6" data-testid="users-table-header-name">
                Name
              </TableHead>
              <TableHead className="px-3 sm:px-4 md:px-6" data-testid="users-table-header-role">
                Role
              </TableHead>
              <TableHead className="hidden sm:table-cell px-3 sm:px-4 md:px-6" data-testid="users-table-header-organisation">
                Organisation
              </TableHead>
              <TableHead className="hidden md:table-cell px-3 sm:px-4 md:px-6" data-testid="users-table-header-site">
                Site
              </TableHead>
              <TableHead className="hidden lg:table-cell px-3 sm:px-4 md:px-6" data-testid="users-table-header-created">
                Created
              </TableHead>
              <TableHead className="text-right px-3 sm:px-4 md:px-6" data-testid="users-table-header-actions">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="users-table-body">
            {users.map((user) => {
              const organisationName = user.organisationId
                ? organisationNameById.get(user.organisationId)
                : undefined;

              return (
                <TableRow 
                  key={user.id} 
                  data-testid={`users-table-row-${user.id}`} 
                  id={`user-row-${user.id}`}
                  onClick={(e) => handleRowClick(user, e)}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <TableCell className="px-3 sm:px-4 md:px-6 max-w-0" data-testid={`user-email-${user.id}`}>
                    <div className="truncate" title={user.email}>
                      {user.email}
                    </div>
                    <div className="sm:hidden mt-1 text-xs text-muted-foreground truncate" data-testid={`user-name-mobile-${user.id}`}>
                      {user.firstName || user.lastName
                        ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                        : 'No name'}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell px-3 sm:px-4 md:px-6 whitespace-nowrap" data-testid={`user-name-${user.id}`}>
                    {user.firstName || user.lastName
                      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                      : '-'}
                  </TableCell>
                  <TableCell className="px-3 sm:px-4 md:px-6 whitespace-nowrap" data-testid={`user-role-${user.id}`}>
                    <Badge variant={getRoleBadgeVariant(user.role)} data-testid={`user-role-badge-${user.id}`}>
                      {user.role.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell px-3 sm:px-4 md:px-6 max-w-0" data-testid={`user-organisation-${user.id}`}>
                    <div className="truncate" title={organisationName}>
                      {organisationName ?? '-'}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell px-3 sm:px-4 md:px-6 max-w-0" data-testid={`user-sites-${user.id}`}>
                    {user.sites && user.sites.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {user.sites.map((site) => (
                          <Badge
                            key={site.id}
                            variant="secondary"
                            className="truncate max-w-[120px]"
                            title={site.name}
                            data-testid={`user-site-badge-${user.id}-${site.id}`}
                          >
                            {site.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell px-3 sm:px-4 md:px-6 whitespace-nowrap" data-testid={`user-created-${user.id}`}>
                    {new Date(user.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="px-3 sm:px-4 md:px-6 whitespace-nowrap text-right" data-testid={`user-actions-${user.id}`}>
                    <div className="flex justify-end gap-2 sm:gap-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {user.id !== currentUserId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(user.id)}
                          disabled={isDeleting}
                          className="text-destructive hover:text-destructive"
                          title="Delete user"
                          data-testid={`user-delete-button-${user.id}`}
                          id={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="hidden sm:inline ml-1">Delete</span>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

